import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { formatearFecha } from '../formato/fecha.js';
import { ImagenNota } from '../media/ImagenNota.js';
import { SIZES } from '../media/srcset.js';

export interface FilaNotaMiniaturaProps {
  nota: NotaPublicaCard;
  /** Posición numerada al frente ("Las más leídas" en plantilla C). */
  numero?: number;
}

/**
 * Fila con miniatura 4:3 a la izquierda: la unidad de la plantilla C, de Última hora, del listado de
 * categoría/búsqueda y del pie "Más de". Toda nota tiene portada (el producto lo exige
 * para publicar), así que acá **nunca** hay un titular pelado: si la foto no aporta
 * jerarquía se achica, no se saca.
 *
 * **Sin volanta de categoría**, por lo mismo que `TarjetaNota`: cada uno de esos lugares ya dice de
 * qué es la lista —el rótulo del bloque, el título de la página de la sección, "Más de Tecnología"—
 * y la volanta salía de `categories[0]`, que en una nota con varias categorías nombraba una
 * cualquiera. La categoría se nombra solo en la portada.
 */
export function FilaNotaMiniatura({ nota, numero }: FilaNotaMiniaturaProps) {
  return (
    <Box component="article" sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      {numero !== undefined && (
        <Typography
          component="span"
          aria-hidden="true"
          sx={{
            fontWeight: 800,
            fontSize: 22,
            lineHeight: 1,
            color: 'var(--color-cliente)',
            minWidth: 24,
            pt: 0.25,
          }}
        >
          {numero}
        </Typography>
      )}
      <MuiLink
        component={RouterLink}
        to={nota.path}
        sx={{ flexShrink: 0, width: { xs: 96, sm: 132 }, display: 'block' }}
      >
        <ImagenNota imagen={nota.cover} ratio="4:3" sizes={SIZES.miniatura} />
      </MuiLink>
      <Box sx={{ minWidth: 0 }}>
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography
            variant="titularTarjeta"
            component="h3"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.title}
          </Typography>
        </MuiLink>
        <Typography variant="firma" component="p" sx={{ color: neutral[700], mt: 0.5 }}>
          {nota.author} · {formatearFecha(nota.publishedAt)}
        </Typography>
      </Box>
    </Box>
  );
}
